import { useState, useEffect } from "react";
import { AdminLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";
import { 
  BarChart3, 
  Facebook, 
  Chrome, 
  Settings, 
  Eye, 
  EyeOff,
  Save,
  RefreshCw,
  Activity,
  Bug,
  Globe,
  Lock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTracking() {
  const [config, setConfig] = useState({
    meta_pixel_id: "",
    meta_access_token: "",
    meta_test_event_code: "",
    ga4_measurement_id: "",
    gtm_container_id: "",
    tracking_enabled: false,
    debug_mode: false,
    environment: "test"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/tracking-config`);
      setConfig({
        meta_pixel_id: response.data.meta_pixel_id || "",
        meta_access_token: response.data.meta_access_token || "",
        meta_test_event_code: response.data.meta_test_event_code || "",
        ga4_measurement_id: response.data.ga4_measurement_id || "",
        gtm_container_id: response.data.gtm_container_id || "",
        tracking_enabled: response.data.tracking_enabled || false,
        debug_mode: response.data.debug_mode || false,
        environment: response.data.environment || "test"
      });
    } catch (error) {
      console.error("Error fetching tracking config:", error);
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/tracking-config`, config);
      toast.success("Configuración guardada correctamente");
    } catch (error) {
      console.error("Error saving tracking config:", error);
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <AdminLayout title="Tracking & Analytics">
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Tracking & Analytics">
      <div className="space-y-6">
        {/* Status Banner */}
        <Card className={config.tracking_enabled ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Activity className={`w-5 h-5 ${config.tracking_enabled ? "text-green-600" : "text-amber-600"}`} />
                <div>
                  <p className={`font-medium ${config.tracking_enabled ? "text-green-800" : "text-amber-800"}`}>
                    {config.tracking_enabled ? "Tracking Activo" : "Tracking Desactivado"}
                  </p>
                  <p className={`text-sm ${config.tracking_enabled ? "text-green-600" : "text-amber-600"}`}>
                    Entorno: {config.environment === "production" ? "Producción" : "Pruebas"}
                    {config.debug_mode && " (Debug Mode)"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label htmlFor="tracking-toggle" className="text-sm">Activar Tracking</Label>
                  <Switch
                    id="tracking-toggle"
                    checked={config.tracking_enabled}
                    onCheckedChange={(checked) => updateConfig("tracking_enabled", checked)}
                    data-testid="tracking-toggle"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="meta" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="meta" className="flex items-center gap-2">
              <Facebook className="w-4 h-4" />
              Meta
            </TabsTrigger>
            <TabsTrigger value="google" className="flex items-center gap-2">
              <Chrome className="w-4 h-4" />
              Google
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              General
            </TabsTrigger>
          </TabsList>

          {/* Meta Tab */}
          <TabsContent value="meta">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Facebook className="w-5 h-5 text-blue-600" />
                  Meta (Facebook/Instagram)
                </CardTitle>
                <CardDescription>
                  Configura el Meta Pixel y la Conversion API (CAPI) para tracking de eventos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="meta-pixel">Pixel ID</Label>
                  <Input
                    id="meta-pixel"
                    value={config.meta_pixel_id}
                    onChange={(e) => updateConfig("meta_pixel_id", e.target.value)}
                    placeholder="123456789012345"
                    className="mt-1"
                    data-testid="meta-pixel-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Encuéntralo en Meta Events Manager → Data Sources → Tu Pixel
                  </p>
                </div>

                <div>
                  <Label htmlFor="meta-token">Access Token (Conversion API)</Label>
                  <div className="relative mt-1">
                    <Input
                      id="meta-token"
                      type={showToken ? "text" : "password"}
                      value={config.meta_access_token}
                      onChange={(e) => updateConfig("meta_access_token", e.target.value)}
                      placeholder="EAAxxxxxxx..."
                      className="pr-10"
                      data-testid="meta-token-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowToken(!showToken)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-stone-500 mt-1">
                    Genera un token en Events Manager → Settings → Conversions API → Generate Access Token
                  </p>
                </div>

                <div>
                  <Label htmlFor="meta-test-code">Test Event Code (opcional)</Label>
                  <Input
                    id="meta-test-code"
                    value={config.meta_test_event_code}
                    onChange={(e) => updateConfig("meta_test_event_code", e.target.value)}
                    placeholder="TEST12345"
                    className="mt-1"
                    data-testid="meta-test-code-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Solo para pruebas. Los eventos aparecerán en Test Events en Meta Events Manager
                  </p>
                </div>

                {/* Events Info */}
                <div className="bg-blue-50 rounded-lg p-4 mt-4">
                  <p className="text-sm font-medium text-blue-800 mb-2">Eventos configurados:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-blue-700">
                    <div>• PageView</div>
                    <div>• ViewContent (perfil)</div>
                    <div>• Lead (inicio chat)</div>
                    <div>• InitiateCheckout</div>
                    <div>• Purchase</div>
                    <div>• CompleteRegistration</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Google Tab */}
          <TabsContent value="google">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Chrome className="w-5 h-5 text-green-600" />
                  Google Analytics & Tag Manager
                </CardTitle>
                <CardDescription>
                  Configura Google Analytics 4 (GA4) y Google Tag Manager (GTM)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="ga4-id">GA4 Measurement ID</Label>
                  <Input
                    id="ga4-id"
                    value={config.ga4_measurement_id}
                    onChange={(e) => updateConfig("ga4_measurement_id", e.target.value)}
                    placeholder="G-XXXXXXXXXX"
                    className="mt-1"
                    data-testid="ga4-id-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Encuéntralo en Google Analytics → Admin → Data Streams → Tu stream
                  </p>
                </div>

                <div>
                  <Label htmlFor="gtm-id">GTM Container ID</Label>
                  <Input
                    id="gtm-id"
                    value={config.gtm_container_id}
                    onChange={(e) => updateConfig("gtm_container_id", e.target.value)}
                    placeholder="GTM-XXXXXXX"
                    className="mt-1"
                    data-testid="gtm-id-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Encuéntralo en Google Tag Manager → Container ID (arriba a la derecha)
                  </p>
                </div>

                {/* Events Info */}
                <div className="bg-green-50 rounded-lg p-4 mt-4">
                  <p className="text-sm font-medium text-green-800 mb-2">Eventos GA4 configurados:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-green-700">
                    <div>• page_view</div>
                    <div>• view_profile</div>
                    <div>• video_play</div>
                    <div>• video_progress</div>
                    <div>• click_cta</div>
                    <div>• start_checkout</div>
                    <div>• purchase</div>
                    <div>• sign_up</div>
                  </div>
                </div>

                {/* DataLayer Info */}
                <div className="bg-stone-100 rounded-lg p-4">
                  <p className="text-sm font-medium text-stone-800 mb-2 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    DataLayer disponible
                  </p>
                  <p className="text-xs text-stone-600">
                    Los eventos incluyen: user_id, session_id, listener_id, event_name, value.
                    Puedes usar estos datos en GTM para crear triggers y tags personalizados.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Configuración General
                </CardTitle>
                <CardDescription>
                  Configuración global del sistema de tracking
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Environment */}
                <div>
                  <Label>Entorno</Label>
                  <Select
                    value={config.environment}
                    onValueChange={(value) => updateConfig("environment", value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="environment-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="test">
                        <div className="flex items-center gap-2">
                          <Bug className="w-4 h-4 text-amber-500" />
                          Pruebas (Test)
                        </div>
                      </SelectItem>
                      <SelectItem value="production">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-green-500" />
                          Producción
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-stone-500 mt-1">
                    En modo Test, los eventos de Meta usarán el Test Event Code si está configurado
                  </p>
                </div>

                {/* Debug Mode */}
                <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Bug className="w-4 h-4 text-amber-500" />
                      Modo Debug
                    </Label>
                    <p className="text-xs text-stone-500 mt-1">
                      Registra todos los eventos en los logs del servidor para validación
                    </p>
                  </div>
                  <Switch
                    checked={config.debug_mode}
                    onCheckedChange={(checked) => updateConfig("debug_mode", checked)}
                    data-testid="debug-toggle"
                  />
                </div>

                {/* Privacy Info */}
                <div className="border border-stone-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-stone-800 mb-2 flex items-center gap-2">
                    <Lock className="w-4 h-4 text-stone-600" />
                    Privacidad
                  </p>
                  <ul className="text-xs text-stone-600 space-y-1">
                    <li>• Los emails se hashean (SHA256) antes de enviar a Meta</li>
                    <li>• No se envían mensajes ni contenido emocional</li>
                    <li>• Los user_id se hashean para privacidad</li>
                    <li>• Se implementa deduplicación con event_id único</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={fetchConfig}
            disabled={loading}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Recargar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
            data-testid="save-tracking-btn"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Guardar Configuración
              </>
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
